import { EtopsData, EtopsPoint, EtopsPointType, etopsPointTypeToWaypoint, RouteWaypointModel } from '@fe/core';
import { ChartFeature, ChartPolygonGeometry } from '../chart/chart-props';

const Y1 = 0.98; // Fraction of height
const Y2 = 0.91; // Fraction of height

export function etopsToFeatures(
    etops: EtopsData | null,
    planned: [number, RouteWaypointModel][]
): ChartFeature<ChartPolygonGeometry, { tag: string | null }>[] {
    if (!etops || !planned) {
        return [];
    }
    const converter = new EtopsToDataset(etops, planned);
    return converter.convert();
}

class EtopsToDataset {
    private data: ChartFeature<ChartPolygonGeometry, { tag: string | null }>[] = [];
    private planned: [number, RouteWaypointModel][];
    private etops: EtopsData;
    private segment: ChartFeature<ChartPolygonGeometry, { tag: string | null }> | null = null;

    constructor(etops: EtopsData, planned: [number, RouteWaypointModel][]) {
        this.etops = etops;
        this.planned = planned;
    }

    convert(): ChartFeature<ChartPolygonGeometry, { tag: string | null }>[] {
        if (!this.checkData) {
            console.warn('Invalid etops data');
            return [];
        }
        this.data = [];
        const { points } = this.etops;
        for (const point of points) {
            if (point.type === EtopsPointType.etopsEntry) {
                const waypoint = this.findWaypoint(point);
                if (!waypoint) {
                    logWaypointNotFound(point);
                    return [];
                }
                this.beginSegment(waypoint[0], this.getTagAhead(point));
            } else if (point.type === EtopsPointType.etp) {
                const waypoint = this.findWaypoint(point);
                if (!waypoint) {
                    logWaypointNotFound(point);
                    return [];
                }
                if (!this.isSegmentInitialized) {
                    logWrongSequence(point);
                    return [];
                }
                const distance = waypoint[0];
                if (!this.completeSegment(distance, this.getTagBehind(point))) {
                    logWrongSequence(point);
                    return [];
                }
                this.beginSegment(distance, this.getTagAhead(point));
            } else if (point.type === EtopsPointType.etopsExit) {
                const waypoint = this.findWaypoint(point);
                if (!waypoint) {
                    logWaypointNotFound(point);
                    return [];
                }

                if (!this.isSegmentInitialized) {
                    logWrongSequence(point);
                    return [];
                }
                this.completeSegment(waypoint[0], this.getTagBehind(point));
            }
        }

        return this.data;
    }

    private findWaypoint(point: EtopsPoint): [number, RouteWaypointModel] | null {
        return (
            this.planned.find(
                (x) =>
                    x[1].type === etopsPointTypeToWaypoint(point.type) &&
                    x[1]?.position[0] === point.position[0] &&
                    x[1].position[1] === point.position[1]
            ) ?? null
        );
    }

    private beginSegment(startDistance: number, tag?: string | null) {
        this.segment = {
            geometry: {
                type: 'Polygon',
                coordinates: [[startDistance, Y1]]
            },
            properties: { tag: tag ?? null }
        };
    }

    private completeSegment(endDistance: number, tag: string | null): boolean {
        if (!this.segment) {
            return false;
        }

        const x1 = this.segment.geometry.coordinates[0][0];
        const y1 = this.segment.geometry.coordinates[0][1];
        const x2 = endDistance;
        const y2 = Y2;
        this.segment = {
            geometry: {
                type: 'Polygon',
                coordinates: [
                    [x1, y1],
                    [x2, y1],
                    [x2, y2],
                    [x1, y2],
                    [x1, y1]
                ]
            },
            properties: {
                tag: this.segment.properties?.tag ?? tag
            }
        };

        this.data.push(this.segment);
        this.segment = null;

        return true;
    }

    private getTagAhead(point: EtopsPoint): string | null {
        const { airports } = this.etops;
        if (point.aheadNdx == null) {
            return null;
        }

        const apt = airports[point.aheadNdx];
        return apt.icaoId ?? apt.iataId;
    }

    private getTagBehind(point: EtopsPoint): string | null {
        const { airports } = this.etops;
        if (point.behindNdx == null) {
            return null;
        }

        const apt = airports[point.behindNdx];
        return apt.icaoId ?? apt.iataId;
    }

    private checkData(): boolean {
        return !!this.etops.points && !!this.etops.airports && !!this.planned;
    }

    private get isSegmentInitialized() {
        return !!this.segment;
    }
}

function logWaypointNotFound(point: EtopsPoint) {
    console.log('Waypoint related to etops point not found', point);
}

function logWrongSequence(point: EtopsPoint) {
    console.log('Wrong poins sequence', point);
}
